import { zodResolver } from '@hookform/resolvers/zod';
import type { Child } from '@baby-tracker/contracts';
import { calendarDateSchema, childSexSchema, personNameSchema } from '@baby-tracker/validation';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import fieldStyles from '../../ui/fields.module.css';
import { SelectField } from '../../ui/SelectField.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { TextField } from '../../ui/TextField.js';
import {
    useArchiveChild,
    useChildren,
    useCreateChild,
    useUpdateChild,
} from '../../../hooks/useChildren.js';
import { formatCalendarDate } from '../../../lib/labels.js';

const schema = z.object({
    name: personNameSchema,
    dateOfBirth: calendarDateSchema,
    sex: childSexSchema,
});

type Values = z.infer<typeof schema>;

const editSchema = schema.extend({
    adjustedDateOfBirth: z.union([z.literal(''), calendarDateSchema]),
});

type EditValues = z.infer<typeof editSchema>;

const sexOptions = [
    { value: 'unspecified', label: 'Prefer not to say' },
    { value: 'female', label: 'Female' },
    { value: 'male', label: 'Male' },
] as const;

function EditChildForm({
    familyId,
    child,
    onDone,
}: {
    familyId: string;
    child: Child;
    onDone: () => void;
}): ReactElement {
    const updateChild = useUpdateChild(familyId);
    const [serverError, setServerError] = useState<string | null>(null);
    const form = useForm<EditValues>({
        resolver: zodResolver(editSchema),
        defaultValues: {
            name: child.name,
            dateOfBirth: child.dateOfBirth,
            adjustedDateOfBirth: child.adjustedDateOfBirth ?? '',
            sex: child.sex,
        },
    });

    async function onSubmit(values: EditValues): Promise<void> {
        setServerError(null);
        try {
            await updateChild.mutateAsync({
                childId: child.id,
                body: {
                    name: values.name,
                    dateOfBirth: values.dateOfBirth,
                    adjustedDateOfBirth:
                        values.adjustedDateOfBirth === '' ? null : values.adjustedDateOfBirth,
                    sex: values.sex,
                },
            });
            onDone();
        } catch (error) {
            setServerError(errorText(error));
        }
    }

    return (
        <form
            className={fieldStyles.stack}
            noValidate
            aria-label={`Edit ${child.name}`}
            onSubmit={(event) => {
                void form.handleSubmit(onSubmit)(event);
            }}
        >
            <TextField
                label="Name"
                error={form.formState.errors.name?.message}
                {...form.register('name')}
            />
            <TextField
                label="Date of birth"
                type="date"
                error={form.formState.errors.dateOfBirth?.message}
                {...form.register('dateOfBirth')}
            />
            <TextField
                label="Adjusted date of birth (optional)"
                type="date"
                hint="The due date, for babies born early. Leave empty to use the date of birth."
                error={form.formState.errors.adjustedDateOfBirth?.message}
                {...form.register('adjustedDateOfBirth')}
            />
            <SelectField
                label="Sex"
                hint="Used for WHO growth percentiles."
                options={sexOptions}
                error={form.formState.errors.sex?.message}
                {...form.register('sex')}
            />
            <FormError message={serverError} />
            <div className={fieldStyles.row}>
                <Button type="submit" loading={form.formState.isSubmitting}>
                    Save
                </Button>
                <Button variant="ghost" onClick={onDone}>
                    Cancel
                </Button>
            </div>
        </form>
    );
}

export function ChildrenSection({
    familyId,
    canEdit,
}: {
    familyId: string;
    canEdit: boolean;
}): ReactElement {
    const children = useChildren(familyId, true);
    const createChild = useCreateChild(familyId);
    const archiveChild = useArchiveChild(familyId);
    const [confirmingId, setConfirmingId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [serverError, setServerError] = useState<string | null>(null);
    const form = useForm<Values>({
        resolver: zodResolver(schema),
        defaultValues: { name: '', dateOfBirth: '', sex: 'unspecified' },
    });

    async function onSubmit(values: Values): Promise<void> {
        setServerError(null);
        try {
            await createChild.mutateAsync(values);
            form.reset({ name: '', dateOfBirth: '', sex: 'unspecified' });
        } catch (error) {
            setServerError(errorText(error));
        }
    }

    return (
        <Card title="Children">
            {children.isError ? <FormError message={errorText(children.error)} /> : null}
            <ul>
                {children.data?.map((child) => (
                    <li key={child.id}>
                        {editingId === child.id ? (
                            <EditChildForm
                                familyId={familyId}
                                child={child}
                                onDone={() => {
                                    setEditingId(null);
                                }}
                            />
                        ) : (
                            <>
                                <p>
                                    {child.name}{' '}
                                    {child.archivedAt ? (
                                        <StatusBadge tone="neutral">Archived</StatusBadge>
                                    ) : null}
                                </p>
                                <p>
                                    Born {formatCalendarDate(child.dateOfBirth)}
                                    {child.adjustedDateOfBirth
                                        ? ` · adjusted ${formatCalendarDate(child.adjustedDateOfBirth)}`
                                        : ''}
                                    {child.sex === 'unspecified' ? '' : ` · ${child.sex}`}
                                </p>
                                {canEdit && !child.archivedAt ? (
                                    confirmingId === child.id ? (
                                        <div className={fieldStyles.row}>
                                            <Button
                                                variant="danger"
                                                loading={archiveChild.isPending}
                                                onClick={() => {
                                                    void archiveChild
                                                        .mutateAsync(child)
                                                        .then(() => {
                                                            setConfirmingId(null);
                                                        });
                                                }}
                                            >
                                                Confirm archive
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                onClick={() => {
                                                    setConfirmingId(null);
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className={fieldStyles.row}>
                                            <Button
                                                variant="secondary"
                                                onClick={() => {
                                                    setEditingId(child.id);
                                                }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                onClick={() => {
                                                    setConfirmingId(child.id);
                                                }}
                                            >
                                                Archive
                                            </Button>
                                        </div>
                                    )
                                ) : null}
                            </>
                        )}
                    </li>
                ))}
            </ul>
            {canEdit ? (
                <form
                    className={fieldStyles.stack}
                    noValidate
                    onSubmit={(event) => {
                        void form.handleSubmit(onSubmit)(event);
                    }}
                >
                    <h3>Add a child</h3>
                    <TextField
                        label="Name"
                        error={form.formState.errors.name?.message}
                        {...form.register('name')}
                    />
                    <TextField
                        label="Date of birth"
                        type="date"
                        error={form.formState.errors.dateOfBirth?.message}
                        {...form.register('dateOfBirth')}
                    />
                    <SelectField
                        label="Sex"
                        options={sexOptions}
                        error={form.formState.errors.sex?.message}
                        {...form.register('sex')}
                    />
                    <FormError message={serverError} />
                    <Button type="submit" loading={form.formState.isSubmitting}>
                        Add child
                    </Button>
                </form>
            ) : null}
        </Card>
    );
}
